import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
    Typography, 
    Button, 
    Box,
    Paper,
    CircularProgress,
    IconButton,
    Tooltip,
    Fade,
    makeStyles
} from '@material-ui/core';
import { Fullscreen, FullscreenExit } from '@material-ui/icons';
import { useStateValue } from '../../context/state/provider';
import { WIZARD_STEPS } from '../../constants/wizardSteps.constants';
import { getUserToken } from '../../services/api';
import { auth } from '../../services/firebase/firebase';

const useStyles = makeStyles((theme) => ({
    stepContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2.5),
        minHeight: 400
    },
    header: {
        textAlign: 'center',
        marginBottom: theme.spacing(0.5)
    },
    title: {
        fontSize: '1.35rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
        letterSpacing: '-0.01em',
        marginBottom: theme.spacing(0.75)
    },
    agentName: {
        color: theme.palette.primary.main,
        fontWeight: 600
    },
    description: {
        color: theme.palette.text.secondary,
        fontSize: '0.95rem',
        lineHeight: 1.5,
        maxWidth: 480,
        margin: '0 auto'
    },
    iframeContainer: {
        border: '1px solid #E5E7EB',
        borderRadius: 12,
        overflow: 'hidden',
        height: 'min(70vh, 640px)',
        minHeight: 520,
        position: 'relative',
        transition: 'all 0.3s ease-in-out',
        backgroundColor: '#fff',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)'
    },
    iframeContainerExpanded: {
        height: 'calc(100vh - 120px)',
        minHeight: 'calc(100vh - 120px)',
        borderRadius: theme.spacing(0.5)
    },
    iframe: {
        width: '100%',
        height: '100%',
        border: 'none',
        display: 'block'
    },
    expandControls: {
        position: 'absolute',
        top: 8,
        right: 8,
        zIndex: 1000,
        display: 'flex',
        gap: theme.spacing(0.5)
    },
    expandButton: {
        backgroundColor: 'rgba(255, 255, 255, 0.92)',
        border: '1px solid #E5E7EB',
        boxShadow: '0 1px 4px rgba(15, 23, 42, 0.08)',
        '&:hover': {
            backgroundColor: '#fff'
        }
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        backgroundColor: 'rgba(255, 255, 255, 0.92)',
        zIndex: 1
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        marginTop: theme.spacing(1),
        flexWrap: 'wrap'
    },
    secondaryActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1)
    },
    textButton: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
        fontWeight: 500,
        padding: theme.spacing(1, 1.5),
        '&:hover': {
            backgroundColor: 'transparent',
            color: theme.palette.primary.main
        }
    },
    nextButton: {
        minWidth: 160,
        padding: theme.spacing(1.25, 3),
        fontSize: '0.95rem',
        fontWeight: 600,
        borderRadius: 8,
        boxShadow: 'none',
        '&:hover': {
            boxShadow: 'none'
        }
    },
    customTooltip: {
        backgroundColor: theme.palette.grey[800],
        color: theme.palette.common.white,
        fontSize: '12px',
        borderRadius: theme.spacing(0.5),
        padding: theme.spacing(1.25)
    },
    errorPanel: {
        maxWidth: 420,
        textAlign: 'center',
        padding: theme.spacing(2)
    },
    errorActions: {
        display: 'flex',
        justifyContent: 'center',
        gap: theme.spacing(1),
        marginTop: theme.spacing(2)
    }
}));

export default function ChatbotTestStep({ checkTokenExpiration }) {
    const classes = useStyles();
    const [{ wizardData, expandedMode }, dispatch] = useStateValue();
    const [loading, setLoading] = useState(true);
    const [chatbotUrl, setChatbotUrl] = useState('');
    const [iframeError, setIframeError] = useState(false);
    const [loadingTimeout, setLoadingTimeout] = useState(null);
    const [userToken, setUserToken] = useState('');

    const iframeRef = useRef(null);

    const fetchUserToken = async () => {
        try {
            const userCredential = await auth.signInAnonymously();
            const uid = userCredential.user.uid;
            const response = await getUserToken({ uid });
            setUserToken(response.data);
        } catch (error) {
            console.error('Error fetching user token:', error);
        }
    };

    useEffect(() => {
        fetchUserToken();
    }, []);

    useEffect(() => {
        const isDemo = !wizardData.chatbotId || wizardData.chatbotId.startsWith('demo-chatbot');
        if (!isDemo && !userToken) {
            setLoading(true);
            return;
        }

        const chatbotAppUrl = process.env.REACT_APP_CHATBOT_URL || process.env.REACT_APP_CHATBOT_URL_SESSION;
        const fullUrl = !isDemo
            ? `${chatbotAppUrl}&token=${userToken}&id=${wizardData.chatbotId}`
            : chatbotAppUrl;

        setChatbotUrl(fullUrl);
        setLoading(false);
        setIframeError(false);

        const timeout = setTimeout(() => {
            setIframeError(true);
        }, 20000);
        setLoadingTimeout(timeout);

        return () => clearTimeout(timeout);
    }, [wizardData.chatbotId, userToken]);

    const postBypassDraftMessage = useCallback(() => {
        try {
            if (!iframeRef.current) return;
            const targetOrigin = new URL(chatbotUrl).origin;
            iframeRef.current.contentWindow.postMessage("chatbotStatus", targetOrigin);
        } catch (err) {
            console.error('Error posting chatbotStatus to iframe:', err);
        }
    }, [chatbotUrl]);

    useEffect(() => {
        const handleIframeMessage = (e) => {
            try {
                const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
                if (data && data.appMounted === "true") {
                    postBypassDraftMessage();
                }
            } catch (err) {
                // Ignore non-JSON messages
            }
        };
        window.addEventListener('message', handleIframeMessage);
        return () => window.removeEventListener('message', handleIframeMessage);
    }, [postBypassDraftMessage]);
    
    useEffect(() => {
        return () => {
            if (loadingTimeout) {
                clearTimeout(loadingTimeout);
            }
        };
    }, [loadingTimeout]);

    const handleMakeOnline = () => {
        if (checkTokenExpiration && !checkTokenExpiration()) {
            return;
        }

        dispatch({
            type: 'UPDATE_WIZARD_DATA',
            data: { chatbotUrl }
        });

        dispatch({
            type: 'SET_CURRENT_STEP',
            step: WIZARD_STEPS.USER_DETAILS
        });
    };

    const handleToggleExpand = () => {
        dispatch({ type: 'TOGGLE_EXPANDED_MODE' });
    };

    const handleNeedHelp = () => {
        dispatch({
            type: 'SET_PREVIOUS_STEP',
            step: WIZARD_STEPS.CHATBOT_TEST
        });
        dispatch({
            type: 'SET_CURRENT_STEP',
            step: WIZARD_STEPS.WHITE_GLOVE_SERVICE
        });
    };

    const agentLabel = wizardData.chatbotName || 'your agent';

    return (
        <Box className={classes.stepContainer}>
            {!expandedMode && (
                <Box className={classes.header}>
                    <Typography className={classes.title}>
                        Try <span className={classes.agentName}>{agentLabel}</span>
                    </Typography>
                    <Typography className={classes.description}>
                        Ask a few questions about your content. When it looks right, continue to publish.
                    </Typography>
                </Box>
            )}

            <Paper className={`${classes.iframeContainer} ${expandedMode ? classes.iframeContainerExpanded : ''}`} elevation={0}>
                <Fade in={!loading}>
                    <Box className={classes.expandControls}>
                        <Tooltip 
                            key={`focus-${expandedMode}`} 
                            title={expandedMode ? "Exit full view" : "Full view"}
                            classes={{ tooltip: classes.customTooltip }}
                            placement="left"
                        >
                            <IconButton 
                                size="small" 
                                className={classes.expandButton}
                                onClick={handleToggleExpand}
                                aria-label={expandedMode ? "Exit full view" : "Enter full view"}
                            >
                                {expandedMode ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Fade>

                {loading && (
                    <Box className={classes.loadingOverlay}>
                        <CircularProgress size={28} />
                        <Typography variant="body2" color="textSecondary">
                            Loading preview…
                        </Typography>
                    </Box>
                )}
                
                {!loading && !iframeError && (
                    <iframe
                        ref={iframeRef}
                        src={chatbotUrl}
                        className={classes.iframe}
                        title={`${agentLabel} preview`}
                        allow="microphone; camera"
                        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                        onError={() => setIframeError(true)}
                        onLoad={() => {
                            if (loadingTimeout) {
                                clearTimeout(loadingTimeout);
                                setLoadingTimeout(null);
                            }
                            setIframeError(false);
                            postBypassDraftMessage();
                            setTimeout(() => postBypassDraftMessage(), 500);
                        }}
                    />
                )}
                
                {!loading && iframeError && (
                    <Box className={classes.loadingOverlay}>
                        <Box className={classes.errorPanel}>
                            <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600 }}>
                                Preview unavailable
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                We couldn’t load the chat preview. Your agent was still created — you can retry or continue to publish.
                            </Typography>
                            <Box className={classes.errorActions}>
                                <Button
                                    variant="outlined"
                                    onClick={() => setIframeError(false)}
                                >
                                    Retry
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disableElevation
                                    onClick={() => {
                                        if (checkTokenExpiration && !checkTokenExpiration()) {
                                            return;
                                        }
                                        dispatch({
                                            type: 'SET_CURRENT_STEP',
                                            step: WIZARD_STEPS.USER_DETAILS
                                        });
                                    }}
                                >
                                    Continue
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                )}
            </Paper>

            {!expandedMode && (
                <Box className={classes.actions}>
                    <Box className={classes.secondaryActions}>
                        <Button
                            className={classes.textButton}
                            onClick={handleNeedHelp}
                        >
                            Need help?
                        </Button>
                    </Box>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleMakeOnline}
                        className={classes.nextButton}
                        disabled={loading}
                        disableElevation
                    >
                        Continue to publish
                    </Button>
                </Box>
            )}
        </Box>
    );
}
